import type { Ref } from 'vue'

import type { AppLocale } from '@/shared/i18n'
import { resolveClient } from '@/shared/ports/resolveClient'
import {
  calendarWindowEnd,
  startOfLocalWeek,
  weekStartsOnFor,
} from '../../lib/raidCalendar'
import {
  RaidsWindowDocument,
} from '../../lib/handWrittenGraphql'
import type { RaidsWindowQuery } from '../../lib/handWrittenGraphql'
import type { RaidSummary } from '../../types'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

function snowflakeVar(s: string): string {
  return s
}

/**
 * Map the wire `RaidsWindow` rows directly to `RaidSummary`. Used by
 * the calendar drawer so the chips render without a follow-up
 * `loadRaids` fetch.
 */
function mapRaidsWindowRows(
  rows: ReadonlyArray<{
    readonly id: number
    readonly guildId: number
    readonly name: string
    readonly isPublished: boolean
    readonly scheduledStartsAt: string | null
    readonly durationMinutes: number | null
    readonly cancelledAt: string | null
    readonly completedAt: string | null
    readonly sourceRaidId: number | null
    readonly recurrence: {
      readonly seriesId: number
      readonly parentId: number
      readonly index: number
      readonly total: number
      readonly frequency: string
    } | null
    readonly notes: string
    readonly createdBy: number | null
    readonly createdAt: string
    readonly updatedAt: string
  }>,
): ReadonlyArray<RaidSummary> {
  return rows.map((r) => ({
    id: String(r.id),
    guildId: String(r.guildId),
    name: r.name,
    isPublished: r.isPublished,
    scheduledStartsAt: r.scheduledStartsAt,
    durationMinutes: r.durationMinutes,
    cancelledAt: r.cancelledAt,
    completedAt: r.completedAt,
    sourceRaidId: r.sourceRaidId === null ? null : String(r.sourceRaidId),
    recurrence: r.recurrence === null
      ? null
      : {
          seriesId: String(r.recurrence.seriesId),
          parentId: String(r.recurrence.parentId),
          index: r.recurrence.index,
          total: r.recurrence.total,
          frequency: r.recurrence.frequency,
        },
    notes: r.notes,
    createdBy: r.createdBy === null ? null : String(r.createdBy),
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  }))
}

export interface CalendarActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly calendarWindowStart: Ref<Date>
  readonly raidsForWindow: Ref<ReadonlyArray<RaidSummary>>
  /**
   * Reactive application locale. `loadRaidsForWindow` re-snaps the
   * GraphQL `from`/`to` range every fetch so the half-open week window
   * stays aligned with the grid column ordering the user is looking at.
   */
  readonly locale: Readonly<Ref<AppLocale>>
}

export interface CalendarActionsApi {
  readonly loadRaidsForWindow: (start: Date) => Promise<void>
  readonly refreshCalendarWindow: () => Promise<void>
  readonly setCalendarWindowStart: (date: Date) => Promise<void>
  readonly goToCurrentWeek: () => Promise<void>
}

export function useCalendarActions(ctx: CalendarActionsCtx): CalendarActionsApi {
  let lastRequestedGuildId: string | null = null
  let requestGeneration = 0

  async function loadRaidsForWindow(start: Date): Promise<void> {
    const guildId = ctx.activeDiscordGuildId.value
    const generation = ++requestGeneration
    if (guildId === '0') {
      lastRequestedGuildId = guildId
      ctx.raidsForWindow.value = []
      return
    }
    if (lastRequestedGuildId !== guildId) {
      lastRequestedGuildId = guildId
      ctx.raidsForWindow.value = []
    }
    const startsOn = startOfLocalWeek(start, weekStartsOnFor(ctx.locale.value))
    ctx.calendarWindowStart.value = startsOn
    const endsOn = calendarWindowEnd(startsOn, weekStartsOnFor(ctx.locale.value))
    try {
      const res = await apolloClient.query<RaidsWindowQuery>({
        query: RaidsWindowDocument,
        variables: {
          guildId: snowflakeVar(guildId),
          from: startsOn.toISOString(),
          to: endsOn.toISOString(),
        } as never,
        fetchPolicy: 'network-only',
      })
      if (
        generation !== requestGeneration
        || guildId !== ctx.activeDiscordGuildId.value
      ) return
      ctx.raidsForWindow.value = mapRaidsWindowRows(res.data?.raidsWindow ?? [])
    } catch (e: unknown) {
      if (
        generation !== requestGeneration
        || guildId !== ctx.activeDiscordGuildId.value
      ) return
      ctx.error.value = e instanceof Error ? e.message : String(e)
    }
  }

  async function refreshCalendarWindow(): Promise<void> {
    await loadRaidsForWindow(ctx.calendarWindowStart.value)
  }

  async function setCalendarWindowStart(date: Date): Promise<void> {
    await loadRaidsForWindow(date)
  }

  async function goToCurrentWeek(): Promise<void> {
    await loadRaidsForWindow(new Date())
  }

  return {
    loadRaidsForWindow,
    refreshCalendarWindow,
    setCalendarWindowStart,
    goToCurrentWeek,
  }
}
